import { createBrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AppLayout } from './AppLayout';
import { RouteErrorPage } from './RouteErrorPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { authRoutes } from './routes/auth.routes';
import { getStartedRoutes } from './routes/get-started.routes';
import { dashboardRoutes } from './routes/dashboard.routes';
import { accountRoutes } from './routes/account.routes';
import { dtesRoutes } from './routes/dtes.routes';
import { boletasRoutes } from './routes/boletas.routes';
import { foliosRoutes } from './routes/folios.routes';
import { enviosRoutes } from './routes/envios.routes';
import { certificationRoutes } from './routes/certification.routes';
import { companiesRoutes } from './routes/companies.routes';
import { inboundRoutes } from './routes/inbound.routes';
import { integrationsRoutes } from './routes/integrations.routes';
import { webhooksRoutes } from './routes/webhooks.routes';
import { legacyRedirectRoutes } from './routes/legacy-redirects.routes';

/**
 * THIS FILE ONLY COMPOSES. No screen registers itself here: each feature owns
 * its `routes/<feature>.routes.tsx` and this module assembles them.
 *
 * The 32 remaining screens would all have had to write into one array, and with
 * agents building them in parallel that is not a risk of conflict but a
 * guaranteed one — and of the worst kind: two adjacent entries added on
 * different branches merge "cleanly" and can duplicate or lose a route with git
 * saying nothing. Split by feature, each screen touches a file nobody else
 * opens.
 *
 * ## The routes are lazy-loaded, not loaded up front
 *
 * Each route declares `lazy: () => import('…')` instead of an `element` built at
 * module load. The bundle was **a single 971 kB chunk** (288 kB gzipped):
 * whoever opened the login screen downloaded all 35 views, the DTE line editor
 * and the payload renderer included.
 *
 * It is the data router's `lazy` and not `React.lazy`, for a concrete reason:
 * react-router resolves the promise **during navigation**, before rendering, so
 * no per-route `<Suspense>` is needed and there is no fallback flash between
 * screens. `element` disappears from the route object but `path` does not, so
 * `router.test.tsx` — which checks that every menu destination resolves to a
 * real route — keeps working unchanged.
 *
 * **Granularity: one per PAGE, not one per feature.** The roadmap suggested one
 * per `<feature>.routes.tsx`; one per page is finer and costs no more code (the
 * `import()` goes where the static import already was). Vite extracts only what
 * is shared between pages into common chunks, so two views of the same feature
 * do not duplicate their domain code.
 *
 * What is NOT lazy: `AppLayout` and the three `auth` routes. The layout is
 * always needed and the auth ones are the first screen — deferring them would
 * add a network round trip right where it shows.
 */
/**
 * `createBrowserRouter` wrapped by Sentry. It is what makes a navigation
 * transaction be named by the route's PATTERN (`/dtes/:id`) and not by the
 * concrete URL (`/dtes/9f0a…`): without it every document opened would be a
 * separate transaction and there would be nothing to aggregate or compare.
 *
 * It wraps the function, not the already-created router, because the
 * instrumentation needs to see the route tree as it is built.
 */
const createRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = createRouter([
  ...authRoutes,
  {
    path: '/',
    element: <AppLayout />,
    // Covers a crash in `AppLayout` itself (the sidebar/header shell). It
    // necessarily renders WITHOUT the shell — there is nothing left standing
    // to render it inside of — which is the one case `RouteErrorPage` is
    // allowed to show without `AppLayout` around it.
    errorElement: <RouteErrorPage />,
    children: [
      // Pathless: it adds no segment of its own, it only gives every screen
      // below an `errorElement` that renders INSIDE `AppLayout`'s `<Outlet
      // />` (sidebar and header stay) instead of replacing the whole shell
      // the way the layout route's own `errorElement` above does.
      {
        errorElement: <RouteErrorPage />,
        children: [
          ...getStartedRoutes,
          ...dashboardRoutes,
          ...accountRoutes,
          ...dtesRoutes,
          ...boletasRoutes,
          ...foliosRoutes,
          ...enviosRoutes,
          ...certificationRoutes,
          ...companiesRoutes,
          ...inboundRoutes,
          ...integrationsRoutes,
          ...webhooksRoutes,
          // LAST: the Spanish URLs this app used to serve, now redirects. They go
          // at the end so a redirect can never shadow the real route it points at —
          // see `legacy-redirects.routes.tsx`.
          ...legacyRedirectRoutes,
          // LAST OF ALL, after the redirects: an unknown path. Putting it
          // after `legacyRedirectRoutes` means a mistyped Spanish URL still
          // has a chance to match a redirect before falling here.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
